import type { ReactNode } from 'react';

import type { AccessLevel, AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { ControlSize } from '../shared/types';
import { ControlSizeProvider } from '../../common/control_size';
import { Badge } from '../../components/Badge/Badge';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { useAccessLevelContext } from '../../utilities/useAccessLevelContext';
import { TerminateButton } from '../TerminateButton/TerminateButton';
import {
  PrimaryFieldDescription,
  PrimaryFieldLabel,
  SecondaryFieldDescription,
  SecondaryFieldLabel,
} from './common/FieldLabel';
import { MaybeFieldContainer } from './FieldContainer';

const FieldHeaderDetails = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
});

const FieldHeaderHeader = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
});

const FieldHeaderBadges = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});

const FieldHeaderLabelling = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});

const FieldHeaderControls = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});

const FieldHeaderContainer = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  padding: '$12 $16',

  '&:not(:last-child)::after': {
    content: '',
    position: 'absolute',
    display: 'block',
    right: '$16',
    bottom: '-0.5px',
    left: '$16',
    height: '1px',
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },
});

export type FieldHeaderProps = {
  /**
   * A set of badges to display on the field.
   */
  badges?: ReactNode;
  /**
   * Whether the field is collapsed.
   */
  collapsed?: boolean;
  /**
   * A set of controls for the field.
   */
  controls?: ReactNode;
  /**
   * A description for the field. Provides a hint such as specific requirements for what to choose.
   */
  description?: ReactNode;
  /**
   * The access level of the field, for displaying access badges.
   */
  accessLevel?: AccessLevelProp;
  /**
   * The label for the header.
   */
  label: ReactNode;
  /**
   * Marks the field as optional with a badge.
   */
  optional?: boolean;
  /**
   * The function to call when the field is collapsed.
   * When provided, the collapsed prop is required.
   */
  setCollapsed?: (collapsed: boolean) => void;
  /**
   * The variand for the header.
   */
  variant?: 'primary' | 'secondary';
};

export function FieldHeader({
  badges,
  collapsed = false,
  setCollapsed,
  controls,
  description,
  label,
  optional,
  accessLevel: accessLevelProp,
  variant = 'primary',
}: FieldHeaderProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp as AccessLevel);

  const handleToggle = () => {
    if (setCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const hasToggle = Boolean(setCollapsed);

  const Label = variant === 'secondary' ? SecondaryFieldLabel : PrimaryFieldLabel;
  const Description = variant === 'secondary' ? SecondaryFieldDescription : PrimaryFieldDescription;

  const variantSize: ControlSize = variant === 'secondary' ? 'x-small' : 'small';

  return (
    <MaybeFieldContainer>
      <FieldHeaderContainer>
        <FieldHeaderDetails>
          <FieldHeaderHeader>
            <FieldHeaderLabelling>
              <AlignStack
                align="start"
                gap={4}
                preset={variant === 'secondary' ? 'small' : 'body'}
                start={
                  hasToggle ? (
                    <TerminateButton
                      onClick={handleToggle}
                      icon={collapsed ? 'chevron-down' : 'chevron-right'}
                      label={collapsed ? 'Collapse' : 'Expand'}
                      variant="neutral"
                    />
                  ) : null
                }
                end={
                  optional || accessLevel || badges ? (
                    <FieldHeaderBadges>
                      {optional && (
                        <Badge ends="card" size="small" variant="neutral">
                          Optional
                        </Badge>
                      )}
                      {accessLevel && <FeatureBadge type={accessLevel} size="x-small" />}
                      {badges && (
                        <ControlSizeProvider value={variantSize}>{badges}</ControlSizeProvider>
                      )}
                    </FieldHeaderBadges>
                  ) : null
                }
              >
                <Label clickable={hasToggle} onClick={hasToggle ? handleToggle : undefined}>
                  {label}
                </Label>
              </AlignStack>
            </FieldHeaderLabelling>
            {controls && (
              <FieldHeaderControls>
                <ControlSizeProvider value={variantSize}>{controls}</ControlSizeProvider>
              </FieldHeaderControls>
            )}
          </FieldHeaderHeader>
          {description && <Description>{description}</Description>}
        </FieldHeaderDetails>
      </FieldHeaderContainer>
    </MaybeFieldContainer>
  );
}
